import React, { useEffect, useState } from 'react';
import { Box, Container, IconButton, Link, Stack, SvgIcon, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router';

import LockIcon from '../../lib/icons/svg/lock.svg?react';
import EyeOpenIcon from '../../lib/icons/svg/eye_open.svg?react';
import EyeClosedIcon from '../../lib/icons/svg/eye_closed.svg?react';
import { usePxtraApiClient } from '../../hooks/usePxtraApiClient';
import { useStore } from '../../hooks/useStore';
import { AgreementDialog, Checkbox, Form, FormTextField } from '../../lib/components';
import { Button } from '../../lib/components/atomic/button';
import { link } from '../../misc/globals';
import { FormLanguageSelect } from '../../lib/components/compound/forms/form/fields/language';
import { FormikValues } from 'formik';
import { LegalDocument, ApiError } from '@pxtra-client-lib-legacy/gen';
import { useSnackbar } from 'notistack';
import { ErrorObject } from '../../stores/authenticationStore';
import { LoginLogo } from './partials/LoginLogo';

const ResetPassword = ({ forEmployee }: { forEmployee: boolean }) => {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const apiClient = usePxtraApiClient();
  const { employeeStore, authenticationStore } = useStore();
  const navigate = useNavigate();
  const snackbar = useSnackbar();

  const resetPasswordId = searchParams.get('pwr');
  const employeeId = searchParams.get('id');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [initial, setInitial] = useState(false);
  const [errors, setErrors] = useState<ErrorObject>({});
  const [AGBAgreement, setAGBAgreement] = useState<LegalDocument | undefined>(undefined);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isConsentRequired, setIsConsentRequired] = useState(false);
  const [isAGBAccepted, setIsAGBAccepted] = useState(false);
  const [isPrivacyAccepted, setIsPrivacyAccepted] = useState(false);
  const [isAGBDialogOpen, setIsAGBDialogOpen] = useState(false);

  const canSubmit = !isConsentRequired || (isAGBAccepted && isPrivacyAccepted);

  const lookupResetPasswordTicket = async () => {
    if (!resetPasswordId || !employeeId) {
      setError(t('auth:resetPasswordTicketInvalid'));
      return;
    }

    try {
      let ticket;
      if (forEmployee) {
        ticket = await apiClient.employees.employeesPasswordResetTicketsRetrieve({
          id: resetPasswordId,
          parentEmployee: employeeId
        });
      } else {
        ticket = await apiClient.auth.authPasswordResetsRetrieve({
          id: resetPasswordId,
          userId: employeeId
        });
      }
      setIsConsentRequired(ticket.init ?? false);
      setInitial(ticket.init ?? false);
    } catch {
      setError(t('auth:resetPasswordTicketInvalid'));
    }
  };

  const fetchAGB = async () => {
    const agb = await authenticationStore.fetchAGBAgreement(
      forEmployee ? 'Employee' : 'Company Manager'
    );
    if (agb) setAGBAgreement(agb);
  };

  const submit = async (values: FormikValues) => {
    setSubmitAttempted(true);
    try {
      if (!employeeId || !resetPasswordId || !canSubmit) return;
      if (forEmployee) {
        await employeeStore.setNewPassword(
          employeeId,
          resetPasswordId,
          values.new_password,
          values.confirm_new_password,
          initial ? isAGBAccepted && isPrivacyAccepted : undefined
        );

        return navigate(`/start/${values.language}`);
      } else {
        await authenticationStore.setNewPassword(
          values.new_password,
          values.confirm_new_password,
          resetPasswordId,
          employeeId,
          values.language
        );
        return navigate(`/login`);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        let message = '';
        if ('body' in e && e.body) {
          message =
            e.body.errors?.non_field_errors?.[0] ??
            e.body.non_field_errors?.[0] ??
            e.body.message ??
            '';
        }
        snackbar.enqueueSnackbar({
          variant: 'detailedError',
          message: t('auth:passwordChangeError'),
          details: message
        });
      }
    }
  };

  const validate = async (values: FormikValues) => {
    const errors = await authenticationStore.validatePassword(values);
    setErrors(errors);
  };

  useEffect(() => {
    fetchAGB();
    lookupResetPasswordTicket();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '0 32px 64px 32px',
          margin: '84px auto',
          maxWidth: '500px',
          maxHeight: '100vh',
          width: '100%'
        }}
      >
        <LoginLogo />
        <Container
          maxWidth="sm"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Typography marginBottom="3rem">{t('auth:setPassword')}</Typography>
          {error ? (
            <Typography paragraph textAlign="center">
              {error}
            </Typography>
          ) : (
            <Form
              initialValues={{
                new_password: '',
                confirm_new_password: '',
                language: 'de'
              }}
              validate={validate}
              useCustomButton
              onSubmit={submit}
            >
              <Stack
                spacing={2}
                sx={{
                  '& .MuiInput-root': {
                    borderRadius: '16px'
                  },
                  maxWidth: '350px'
                }}
              >
                <FormLanguageSelect
                  hidden={!isConsentRequired}
                  additionalOnChange={(l) => i18n.changeLanguage((l as string) ?? 'de')}
                  name="language"
                  label={undefined}
                />
                <FormTextField
                  type={showPassword ? 'text' : 'password'}
                  name="new_password"
                  placeholder={t('account:NEW_PASSWORD')}
                  startAdornment={<SvgIcon component={LockIcon} />}
                  endAdornment={
                    <IconButton
                      aria-label={t('auth:showPasswordHint')}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <SvgIcon component={!showPassword ? EyeOpenIcon : EyeClosedIcon} />
                    </IconButton>
                  }
                  errors={errors.new_password}
                />
                <FormTextField
                  type={showPassword ? 'text' : 'password'}
                  name="confirm_new_password"
                  placeholder={`${t('auth:password')} ${t('common:CONFIRM').toLowerCase()}`}
                  startAdornment={<SvgIcon component={LockIcon} />}
                  endAdornment={
                    <IconButton
                      aria-label={t('auth:showPasswordHint')}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <SvgIcon component={!showPassword ? EyeOpenIcon : EyeClosedIcon} />
                    </IconButton>
                  }
                  errors={errors.confirm_new_password}
                />
                <Button variant="primary" className="h-full" type="submit">
                  {t('common:SAVE')}
                </Button>
                {isConsentRequired && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        paddingX: 1,
                        color: 'primary.light',
                        '&:hover': { cursor: 'pointer' }
                      }}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).tagName.toLowerCase() !== 'a') {
                          if (AGBAgreement) setIsAGBDialogOpen(true);
                          else setIsAGBAccepted((accepted) => !accepted);
                        }
                      }}
                    >
                      <Checkbox
                        checked={isAGBAccepted}
                        sx={{ '&.Mui-checked > span': { backgroundColor: 'white' } }}
                        errors={
                          !submitAttempted || isAGBAccepted ? [] : [t('form:FIELD_IS_REQUIRED')]
                        }
                      />
                      <Typography sx={{ flex: 1, paddingX: 1.5, color: 'inherit' }}>
                        <Trans
                          i18nKey="account:TERMS_OF_USE_CONSENT"
                          components={{
                            1: (
                              <Link
                                color="inherit"
                                sx={{ '&:hover': { color: 'grey.100' } }}
                                href={link.employee_agb}
                                target="blank"
                              />
                            )
                          }}
                        />
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        paddingX: 1,
                        color: 'primary.light',
                        '&:hover': { cursor: 'pointer' }
                      }}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).tagName.toLowerCase() !== 'a') {
                          setIsPrivacyAccepted((accepted) => !accepted);
                        }
                      }}
                    >
                      <Checkbox
                        checked={isPrivacyAccepted}
                        sx={{ '&.Mui-checked > span': { backgroundColor: 'white' } }}
                        errors={
                          !submitAttempted || isPrivacyAccepted ? [] : [t('form:FIELD_IS_REQUIRED')]
                        }
                      />
                      <Typography sx={{ flex: 1, paddingX: 1.5, color: 'inherit' }}>
                        <Trans
                          i18nKey="account:DATA_PRIVACY_POLICIES_CONSENT"
                          components={{
                            1: (
                              <Link
                                color="inherit"
                                sx={{ '&:hover': { color: 'grey.100' } }}
                                href={link.privacy_policy}
                                target="blank"
                              />
                            )
                          }}
                        />
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </Form>
          )}
          <AgreementDialog
            open={isAGBDialogOpen}
            onClose={() => setIsAGBDialogOpen(false)}
            title={t('agreements:termsOfUse')}
            contentText={t('agreements:scrollHint', { title: AGBAgreement?.title })}
            contentHtml={AGBAgreement?.content}
            onConfirm={() => {
              setIsAGBAccepted(true);
            }}
            confirmButtonText={t('common:CONFIRM')}
            checkboxText={t('agreements:agreement', { title: AGBAgreement?.title })}
          />
        </Container>
      </Box>
    </Box>
  );
};

export default observer(ResetPassword);
